import React, { memo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TablePagination,
  CircularProgress,
  Box,
  Tooltip,
  Typography,
  useMediaQuery,
  Card,
  CardContent,
  Divider,
  Popover,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTheme } from "@mui/material/styles";

const daysList = [
  { label: "Sun", value: 0 },
  { label: "Mon", value: 1 },
  { label: "Tue", value: 2 },
  { label: "Wed", value: 3 },
  { label: "Thu", value: 4 },
  { label: "Fri", value: 5 },
  { label: "Sat", value: 6 },
];

const CreateJobBatchTable = ({
  jobBatchList = [],
  loader,
  page,
  rowsPerPage,
  total,
  setPage,
  setRowsPerPage,
  handleEdit,
  handleDelete,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  
  // Popover state for recurrence days
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedDays, setSelectedDays] = useState([]);

  const handleOpenDays = (event, days) => {
    setAnchorEl(event.currentTarget);
    setSelectedDays(days || []);
  };

  const handleCloseDays = () => {
    setAnchorEl(null);
  };

  const getDayLabels = (values) => {
    return values
      .map((v) => daysList.find((d) => d.value === v)?.label)
      .filter(Boolean)
      .join(", ");
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  // --- MOBILE CARD VIEW ---
  if (isMobile && !loader) {
    return (
      <Box sx={{ p: 1 }}>
        {jobBatchList?.length > 0 ? (
          jobBatchList.map((row) => (
            <Card key={row.id} sx={{ mb: 2, borderRadius: "12px", boxShadow: 3 }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary">
                    {row.job_master?.job_name}
                  </Typography>
                  <Typography variant="caption" sx={{ bgcolor: '#e3f2fd', p: 0.5, borderRadius: 1 }}>
                    {row.recurrence_type}
                  </Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Typography variant="body2"><strong>Customer:</strong> {row.customer?.contact_name}</Typography>
                <Typography variant="body2"><strong>Staff:</strong> {row.staff?.contact_name}</Typography>
                <Typography variant="body2"><strong>Dates:</strong> {row.start_date?.split("T")[0]} to {row.end_date?.split("T")[0]}</Typography>
                
                <Box display="flex" alignItems="center" mt={1} justifyContent="space-between">
                  <Typography variant="body2"><strong>Priority:</strong> {row.priority}</Typography>
                  <Box>
                    {row.recurrence_type === "custom" && (
                      <IconButton size="small" onClick={(e) => handleOpenDays(e, row.recurrence_days)}>
                        <VisibilityIcon fontSize="small" color="info" />
                      </IconButton>
                    )}
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(row.id)}
                      sx={{
                        backgroundColor: "error.main",
                        color: "white",
                        ml: 0.5,
                        "&:hover": { backgroundColor: "error.dark" },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))
        ) : (
          <Typography align="center">No Data Found</Typography>
        )}
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
        />
        {renderPopover()}
      </Box>
    );
  }

  function renderPopover() {
    return (
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleCloseDays}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Box sx={{ p: 2, bgcolor: "#333", color: "#fff" }}>
          <Typography variant="body2">
            <strong>Recurrence Days:</strong> {getDayLabels(selectedDays)}
          </Typography>
        </Box>
      </Popover>
    );
  }

  return (
    <Paper sx={{ borderRadius: "12px", overflow: "hidden", boxShadow: "0 10px 30px rgba(0,0,0,0.1)" }}>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#1976d2" }}>
              <TableCell sx={{ color: "#fff" }}>#</TableCell>
              <TableCell sx={{ color: "#fff" }}>Job</TableCell>
              <TableCell sx={{ color: "#fff" }}>Customer</TableCell>
              <TableCell sx={{ color: "#fff" }}>Staff</TableCell>
              <TableCell sx={{ color: "#fff" }}>Start</TableCell>
              <TableCell sx={{ color: "#fff" }}>End</TableCell>
              <TableCell sx={{ color: "#fff" }}>Type</TableCell>
              <TableCell sx={{ color: "#fff" }}>Priority</TableCell>
              <TableCell sx={{ color: "#fff" }} align="center">Action</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loader ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : jobBatchList?.length > 0 ? (
              jobBatchList.map((row, index) => (
                <TableRow key={row.id} hover>
                  <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                  <TableCell>{row.job_master?.job_name}</TableCell>
                  <TableCell>{row.customer?.contact_name}</TableCell>
                  <TableCell>{row.staff?.contact_name}</TableCell>
                  <TableCell>{row.start_date?.split("T")[0]}</TableCell>
                  <TableCell>{row.end_date?.split("T")[0]}</TableCell>
                  <TableCell>{row.recurrence_type}</TableCell>
                  <TableCell>{row.priority}</TableCell>

                  <TableCell align="center">
                    {row.recurrence_type === "custom" && (
                      <Tooltip title="View Recurrence Days">
                        <IconButton
                          onClick={(e) => handleOpenDays(e, row.recurrence_days)}
                          color="info"
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Delete Batch">
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(row.id)}
                        sx={{
                          backgroundColor: "error.main",
                          color: "white",
                          ml: 0.5,
                          "&:hover": { backgroundColor: "error.dark" },
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={9} align="center">No Data Found</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      {renderPopover()}
    </Paper>
  );
};

export default memo(CreateJobBatchTable);